import React, { useEffect, useMemo, useState } from 'react';
import {
  Edit,
  Trash2,
  Plus,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Search,
  PiggyBank,
  Wallet,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Camera,
  LineChart,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Compte } from '../lib/services/AccountService';
import AccountForm from './AccountForm';
import { useTransactions } from '../hooks';
import {
  ACCOUNT_TYPE_META,
  AccountTypeFilter,
  accountTypeLabel,
  maskIban,
  normalizeAccountType,
  sumBalances,
} from '../lib/accountTypes';
import { transactionTypeLabel } from '../lib/transactionCashflow';
import { getTransactionAttachmentSignedUrl } from '../lib/services/transactionAttachmentService';
import { snapshotMesSoldes } from '../lib/services/SoldeHistoryService';

type SortColumn = 'nom' | 'type' | 'solde' | 'evolution';
type SortDirection = 'asc' | 'desc';
type SortState = { column: SortColumn | null; direction: SortDirection | null };

const TYPE_SORT_ORDER: Record<ReturnType<typeof normalizeAccountType>, number> = {
  compte_courant: 0,
  epargne: 1,
  credit: 2,
  autre: 3,
};
/** Anneau de progression (évite les artefacts conic-gradient). */
function StatRing({
  percent,
  trackClassName,
  strokeClassName,
  icon: Icon,
  iconClassName,
}: {
  percent: number;
  trackClassName: string;
  strokeClassName: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
}) {
  const value = Math.min(100, Math.max(0, percent));
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (value / 100) * circumference;

  return (
    <div className="relative h-12 w-12 shrink-0 overflow-hidden">
      <svg
        className="h-12 w-12 -rotate-90"
        viewBox="0 0 32 32"
        aria-hidden
      >
        <circle
          cx="16"
          cy="16"
          r={radius}
          fill="none"
          strokeWidth="3"
          stroke="currentColor"
          className={trackClassName}
        />
        <circle
          cx="16"
          cy="16"
          r={radius}
          fill="none"
          strokeWidth="3"
          stroke="currentColor"
          strokeLinecap="round"
          className={strokeClassName}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <Icon className={`h-4 w-4 ${iconClassName}`} />
      </div>
    </div>
  );
}

interface AccountListProps {
  accounts: Compte[];
  loading: boolean;
  onAddAccount: (account: Compte) => Promise<{ success: boolean; error?: string; data?: Compte }>;
  onEditAccount: (
    id: string,
    updates: Partial<Compte>,
  ) => Promise<{ success: boolean; error?: string; data?: Compte }>;
  onDeleteAccount: (id: string) => Promise<{ success: boolean; error?: string }>;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/** Ajoute https:// si l’URL n’a pas de schéma. */
function bankPortalUrl(raw?: string | null): string | null {
  const t = (raw || '').trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function monthBounds(d = new Date()) {
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const prevStart = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const prevEnd = new Date(d.getFullYear(), d.getMonth(), 0);
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  return {
    start: iso(start),
    end: iso(end),
    prevStart: iso(prevStart),
    prevEnd: iso(prevEnd),
  };
}

function signedImpactForAccount(
  tx: { type?: string; montant: number; compte_id?: string; compte_destination_id?: string | null },
  compteId: string,
): number {
  const abs = Math.abs(tx.montant || 0);
  const type = tx.type || 'depense';
  if (type === 'transfert') {
    if (tx.compte_id === compteId) return -abs;
    if (tx.compte_destination_id === compteId) return abs;
    return 0;
  }
  if (tx.compte_id !== compteId) return 0;
  if (type === 'depense' || type === 'pret') return -abs;
  return abs;
}

const AccountList: React.FC<AccountListProps> = ({
  accounts,
  loading,
  onAddAccount,
  onEditAccount,
  onDeleteAccount,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Compte | undefined>(undefined);
  const [filterType, setFilterType] = useState<AccountTypeFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<SortState>({ column: 'nom', direction: 'asc' });
  const [iconUrlByPath, setIconUrlByPath] = useState<Record<string, string>>({});
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const { transactions } = useTransactions();
  const bounds = useMemo(() => monthBounds(), []);

  useEffect(() => {
    let cancelled = false;
    const paths = Array.from(
      new Set(accounts.map((a) => a.attachment_path).filter((p): p is string => !!p)),
    );
    if (paths.length === 0) {
      setIconUrlByPath({});
      return;
    }
    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        paths.map(async (path) => {
          const { url } = await getTransactionAttachmentSignedUrl(path);
          if (url) next[path] = url;
        }),
      );
      if (!cancelled) setIconUrlByPath(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [accounts]);

  const typeCounts = useMemo(() => {
    const counts: Record<Exclude<AccountTypeFilter, 'all'>, number> = {
      compte_courant: 0,
      epargne: 0,
      credit: 0,
      autre: 0,
    };
    for (const a of accounts) {
      counts[normalizeAccountType(a.type)] += 1;
    }
    return counts;
  }, [accounts]);

  const accountMonthDeltas = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of accounts) {
      if (!a.id) continue;
      let delta = 0;
      for (const tx of transactions) {
        if (tx.date < bounds.start || tx.date > bounds.end) continue;
        delta += signedImpactForAccount(tx, a.id);
      }
      map.set(a.id, delta);
    }
    return map;
  }, [accounts, transactions, bounds]);

  const cycleSort = (column: SortColumn) => {
    setSort((prev) => {
      if (prev.column !== column) {
        return { column, direction: column === 'nom' || column === 'type' ? 'asc' : 'desc' };
      }
      if (prev.direction === 'asc') return { column, direction: 'desc' };
      if (prev.direction === 'desc') return { column: 'nom', direction: 'asc' };
      return { column, direction: 'asc' };
    });
  };

  const filteredAccounts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = accounts.filter((a) => {
      if (filterType !== 'all' && normalizeAccountType(a.type) !== filterType) return false;
      if (!q) return true;
      const hay = `${a.nom} ${a.iban || ''} ${a.nom_banque || ''} ${a.description || ''}`.toLowerCase();
      return hay.includes(q);
    });

    const dir = sort.direction === 'desc' ? -1 : 1;
    const column = sort.column || 'nom';

    return [...list].sort((a, b) => {
      if (column === 'type') {
        const byType =
          TYPE_SORT_ORDER[normalizeAccountType(a.type)] -
          TYPE_SORT_ORDER[normalizeAccountType(b.type)];
        if (byType !== 0) return byType * dir;
        return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
      }
      if (column === 'solde') {
        const bySolde = ((a.solde || 0) - (b.solde || 0)) * dir;
        if (bySolde !== 0) return bySolde;
        return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
      }
      if (column === 'evolution') {
        const da = a.id ? accountMonthDeltas.get(a.id) || 0 : 0;
        const db = b.id ? accountMonthDeltas.get(b.id) || 0 : 0;
        const byDelta = (da - db) * dir;
        if (byDelta !== 0) return byDelta;
        return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
      }
      return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }) * dir;
    });
  }, [accounts, filterType, searchTerm, sort, accountMonthDeltas]);

  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sort.column !== column || !sort.direction) {
      return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    }
    return sort.direction === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-primary-400" />
    ) : (
      <ArrowDown className="w-3 h-3 text-primary-400" />
    );
  };

  const SortHeader: React.FC<{
    column: SortColumn;
    label: string;
    align?: 'left' | 'right';
  }> = ({ column, label, align = 'left' }) => (
    <button
      type="button"
      onClick={() => cycleSort(column)}
      className={`inline-flex items-center gap-1 hover:text-white transition ${
        align === 'right' ? 'justify-end w-full' : ''
      } ${sort.column === column ? 'text-white' : ''}`}
      aria-sort={
        sort.column === column
          ? sort.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <span>{label}</span>
      <SortIcon column={column} />
    </button>
  );
  const totalBalance = useMemo(() => sumBalances(accounts), [accounts]);
  const positiveAccounts = useMemo(
    () => accounts.filter((a) => (a.solde || 0) > 0).length,
    [accounts],
  );
  const negativeAccounts = useMemo(
    () => accounts.filter((a) => (a.solde || 0) < 0).length,
    [accounts],
  );

  const monthNet = useMemo(() => {
    return transactions.reduce((sum, tx) => {
      if (tx.date < bounds.start || tx.date > bounds.end) return sum;
      const abs = Math.abs(tx.montant || 0);
      if (tx.type === 'depense' || tx.type === 'pret') return sum - abs;
      if (tx.type === 'revenu' || tx.type === 'remboursement') return sum + abs;
      return sum;
    }, 0);
  }, [transactions, bounds]);

  const prevMonthNet = useMemo(() => {
    return transactions.reduce((sum, tx) => {
      if (tx.date < bounds.prevStart || tx.date > bounds.prevEnd) return sum;
      const abs = Math.abs(tx.montant || 0);
      if (tx.type === 'depense' || tx.type === 'pret') return sum - abs;
      if (tx.type === 'revenu' || tx.type === 'remboursement') return sum + abs;
      return sum;
    }, 0);
  }, [transactions, bounds]);

  const totalBalancePrevApprox = totalBalance - monthNet;
  const balanceDeltaPct =
    Math.abs(totalBalancePrevApprox) > 0.01
      ? ((totalBalance - totalBalancePrevApprox) / Math.abs(totalBalancePrevApprox)) * 100
      : 0;

  const distribution = useMemo(() => {
    const groups: Record<Exclude<AccountTypeFilter, 'all'>, number> = {
      compte_courant: 0,
      epargne: 0,
      credit: 0,
      autre: 0,
    };
    for (const a of accounts) {
      groups[normalizeAccountType(a.type)] += a.solde || 0;
    }
    const absTotal = Object.values(groups).reduce((s, v) => s + Math.abs(v), 0) || 1;
    return (Object.keys(groups) as Array<Exclude<AccountTypeFilter, 'all'>>)
      .map((key) => ({
        key,
        amount: groups[key],
        pct: (Math.abs(groups[key]) / absTotal) * 100,
        meta: ACCOUNT_TYPE_META[key],
      }))
      .filter((g) => typeCounts[g.key] > 0 || Math.abs(g.amount) > 0.009);
  }, [accounts, typeCounts]);

  const recentActivity = useMemo(() => {
    return [...transactions]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 5);
  }, [transactions]);

  const handleEdit = (account: Compte) => {
    setEditingAccount(account);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce compte ? Cette action est irréversible.')) {
      const result = await onDeleteAccount(id);
      if (!result.success) {
        alert(`Erreur lors de la suppression : ${result.error}`);
      }
    }
  };

  const handleFormSubmit = async (account: Compte) => {
    let result: { success: boolean; error?: string; data?: Compte };
    if (editingAccount?.id) {
      result = await onEditAccount(editingAccount.id, account);
    } else {
      result = await onAddAccount(account);
    }

    if (!result.success) {
      alert(`Erreur : ${result.error}`);
    }
    // Fermeture gérée par AccountForm (après upload éventuel de l'icône)
    return result;
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingAccount(undefined);
  };

  const handleSnapshotSoldes = async () => {
    if (snapshotSaving || accounts.length === 0) return;
    setSnapshotSaving(true);
    try {
      const { data, error } = await snapshotMesSoldes();
      if (error || !data) {
        alert(`Impossible d'enregistrer les soldes : ${error?.message || error || 'erreur inconnue'}`);
        return;
      }
      if (data.nothing_to_save) {
        alert('Aucun compte à enregistrer.');
        return;
      }
      const written = data.inserted + data.updated;
      if (written === 0 || data.no_change) {
        alert('Aucun solde n’a changé depuis le dernier enregistrement.');
        return;
      }
      alert(
        `Soldes mis à jour pour le ${data.date} : ${written} compte${written > 1 ? 's' : ''} (inchangés : ${data.unchanged}).`,
      );
    } finally {
      setSnapshotSaving(false);
    }
  };

  const filterChips: { key: AccountTypeFilter; label: string; count?: number }[] = [
    { key: 'all', label: 'Tous les comptes' },
    { key: 'compte_courant', label: 'Courants', count: typeCounts.compte_courant },
    { key: 'epargne', label: 'Épargne', count: typeCounts.epargne },
    { key: 'credit', label: 'Crédit', count: typeCounts.credit },
    { key: 'autre', label: 'Autres', count: typeCounts.autre },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="text-white mt-4">Chargement des comptes...</p>
        </div>
      </div>
    );
  }

  const positivePct = accounts.length ? (positiveAccounts / accounts.length) * 100 : 0;
  const negativePct = accounts.length ? (negativeAccounts / accounts.length) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-white">Comptes bancaires</h2>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-dark-700 text-dark-300">
            {accounts.length} compte{accounts.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Link
            to="/solde-evolution"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors border border-dark-600 bg-dark-800 hover:bg-dark-700 text-white"
            title="Voir les courbes d'évolution"
          >
            <LineChart className="w-4 h-4" />
            <span>Évolution</span>
          </Link>
          <button
            type="button"
            onClick={handleSnapshotSoldes}
            disabled={snapshotSaving || accounts.length === 0}
            title="Enregistre seulement les comptes dont le solde a changé"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors border border-dark-600 bg-dark-800 hover:bg-dark-700 text-white disabled:opacity-50"
          >
            <Camera className="w-4 h-4" />
            <span>{snapshotSaving ? 'Enregistrement…' : 'Enregistrer les soldes'}</span>
          </button>
          <button
            onClick={() => setIsFormOpen(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Nouveau compte</span>
          </button>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
            <p className="text-dark-400 text-sm">Solde total</p>
            <p className="text-2xl font-bold text-white mt-1">{formatCurrency(totalBalance)}</p>
            <p className={`text-xs mt-2 ${balanceDeltaPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {balanceDeltaPct >= 0 ? '+' : ''}
              {balanceDeltaPct.toFixed(2)} % vs mois précédent
            </p>
          </div>

          <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-dark-400 text-sm">Comptes positifs</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{positiveAccounts}</p>
                <p className="text-xs text-dark-400 mt-2">{positivePct.toFixed(1)} % du portefeuille</p>
              </div>
              <StatRing
                percent={positivePct}
                trackClassName="text-emerald-500/20"
                strokeClassName="text-emerald-400"
                icon={TrendingUp}
                iconClassName="text-emerald-400"
              />
            </div>
          </div>

          <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-dark-400 text-sm">Comptes négatifs</p>
                <p className="text-2xl font-bold text-red-400 mt-1">{negativeAccounts}</p>
                <p className="text-xs text-dark-400 mt-2">{negativePct.toFixed(1)} % du portefeuille</p>
              </div>
              <StatRing
                percent={negativePct}
                trackClassName="text-red-500/20"
                strokeClassName="text-red-400"
                icon={TrendingDown}
                iconClassName="text-red-400"
              />
            </div>
          </div>

          <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
            <p className="text-dark-400 text-sm">Mouvement net</p>
            <p className={`text-2xl font-bold mt-1 ${monthNet >= 0 ? 'text-sky-400' : 'text-red-400'}`}>
              {monthNet >= 0 ? '+' : ''}
              {formatCurrency(monthNet)}
            </p>
            <p className="text-xs text-dark-400 mt-2">
              Ce mois
              {Math.abs(prevMonthNet) > 0.01
                ? ` · mois préc. ${prevMonthNet >= 0 ? '+' : ''}${formatCurrency(prevMonthNet)}`
                : ''}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <div className="space-y-4 min-w-0">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {filterChips.map((chip) => {
                const active = filterType === chip.key;
                const hideEmpty =
                  chip.key !== 'all' && typeof chip.count === 'number' && chip.count === 0;
                if (hideEmpty) return null;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setFilterType(chip.key)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors border ${
                      active
                        ? 'bg-primary-500/15 border-primary-500 text-primary-300'
                        : 'bg-dark-800 border-dark-700 text-dark-300 hover:text-white hover:border-dark-500'
                    }`}
                  >
                    <span>{chip.label}</span>
                    {typeof chip.count === 'number' && (
                      <span
                        className={`min-w-[1.25rem] text-center text-[11px] rounded-full px-1.5 py-0.5 ${
                          active ? 'bg-primary-500/30 text-primary-200' : 'bg-dark-700 text-dark-400'
                        }`}
                      >
                        {chip.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un compte..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          <div className="bg-dark-800 border border-dark-700 rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[minmax(0,2fr)_100px_140px_120px_124px] gap-3 px-4 py-3 text-xs uppercase tracking-wide text-dark-400 border-b border-dark-700">
              <SortHeader column="nom" label="Compte" />
              <SortHeader column="type" label="Type" />
              <SortHeader column="solde" label="Solde" align="right" />
              <SortHeader column="evolution" label="Évolution" align="right" />
              <span className="text-right">Actions</span>
            </div>

            {filteredAccounts.length === 0 ? (
              <div className="text-center py-12 px-4">
                <div className="w-14 h-14 bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-dark-400" />
                </div>
                <p className="text-white font-medium mb-1">Aucun compte</p>
                <p className="text-dark-400 text-sm">
                  {accounts.length === 0
                    ? 'Commencez par ajouter votre premier compte bancaire'
                    : 'Aucun résultat pour ce filtre'}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-dark-700">
                {filteredAccounts.map((account) => {
                  const typeKey = normalizeAccountType(account.type);
                  const meta = ACCOUNT_TYPE_META[typeKey];
                  const delta = account.id ? accountMonthDeltas.get(account.id) || 0 : 0;
                  const startApprox = (account.solde || 0) - delta;
                  const evolPct =
                    Math.abs(startApprox) > 0.01 ? (delta / Math.abs(startApprox)) * 100 : 0;
                  const iconUrl = account.attachment_path
                    ? iconUrlByPath[account.attachment_path]
                    : undefined;
                  const portalUrl = bankPortalUrl(account.site_web);
                  return (
                    <li
                      key={account.id}
                      className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_100px_140px_120px_124px] gap-3 px-4 py-3 items-center hover:bg-dark-700/40"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                          style={{ backgroundColor: account.couleur || '#3B82F6' }}
                        >
                          {iconUrl ? (
                            <img src={iconUrl} alt="" className="w-full h-full object-cover" />
                          ) : typeKey === 'epargne' ? (
                            <PiggyBank className="w-5 h-5 text-white" />
                          ) : typeKey === 'credit' ? (
                            <Wallet className="w-5 h-5 text-white" />
                          ) : (
                            <CreditCard className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">
                            {account.nom}
                            {account.is_default ? (
                              <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-primary-400">
                                défaut
                              </span>
                            ) : null}
                          </p>
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="text-xs text-dark-400 truncate">{maskIban(account.iban)}</p>
                            {portalUrl && (
                              <a
                                href={portalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 shrink-0 text-xs text-primary-400 hover:text-primary-300"
                                title="Ouvrir le portail bancaire"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                <span>Banque</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs ${meta.badgeClass}`}>
                          {accountTypeLabel(account.type)}
                        </span>
                      </div>

                      <div className="md:text-right">
                        <p
                          className={`font-semibold ${
                            (account.solde || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {formatCurrency(account.solde || 0)}
                        </p>
                        {account.solde_initial !== undefined && (
                          <p className="text-[11px] text-dark-500">
                            Initial {formatCurrency(account.solde_initial)}
                          </p>
                        )}
                      </div>

                      <div className="md:text-right">
                        <p className={`text-sm ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {delta >= 0 ? '+' : ''}
                          {formatCurrency(delta)}
                        </p>
                        <p className="text-[11px] text-dark-500">
                          {evolPct >= 0 ? '+' : ''}
                          {evolPct.toFixed(1)} % ce mois
                        </p>
                      </div>

                      <div className="flex items-center justify-end gap-1 shrink-0">
                        {portalUrl && (
                          <a
                            href={portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-primary-400 hover:text-primary-300 hover:bg-primary-500/10 rounded-lg transition-colors"
                            title="Ouvrir le portail bancaire"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => handleEdit(account)}
                          className="p-2 text-dark-400 hover:text-white hover:bg-dark-600 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => account.id && handleDelete(account.id)}
                          className="p-2 text-dark-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4">Répartition des soldes</h3>
            {distribution.length === 0 ? (
              <p className="text-sm text-dark-400">Aucune donnée</p>
            ) : (
              <>
                <div
                  className="mx-auto w-36 h-36 rounded-full mb-4"
                  style={{
                    background: `conic-gradient(${distribution
                      .map((d, i) => {
                        const start = distribution
                          .slice(0, i)
                          .reduce((s, x) => s + x.pct, 0);
                        return `${d.meta.chartColor} ${start}% ${start + d.pct}%`;
                      })
                      .join(', ')})`,
                    maskImage: 'radial-gradient(transparent 52%, black 54%)',
                    WebkitMaskImage: 'radial-gradient(transparent 52%, black 54%)',
                  }}
                />
                <ul className="space-y-2">
                  {distribution.map((d) => (
                    <li key={d.key} className="flex items-center justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2 text-dark-300 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: d.meta.chartColor }}
                        />
                        <span className="truncate">{d.meta.label.replace('Compte ', 'Comptes ')}</span>
                      </span>
                      <span className="text-right shrink-0">
                        <span className="text-white block">{formatCurrency(d.amount)}</span>
                        <span className="text-[11px] text-dark-500">{d.pct.toFixed(1)} %</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4">Activité récente</h3>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-dark-400">Aucune transaction récente</p>
            ) : (
              <ul className="space-y-3">
                {recentActivity.map((tx) => {
                  const amount = Math.abs(tx.montant || 0);
                  const isOut =
                    tx.type === 'depense' || tx.type === 'pret' || tx.type === 'transfert';
                  return (
                    <li key={tx.id} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{tx.description}</p>
                        <p className="text-[11px] text-dark-500">
                          {transactionTypeLabel(tx.type || 'depense')} · {tx.date}
                        </p>
                      </div>
                      <p
                        className={`text-sm font-medium shrink-0 ${
                          isOut ? 'text-red-400' : 'text-emerald-400'
                        }`}
                      >
                        {isOut ? '-' : '+'}
                        {formatCurrency(amount)}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>

      <AccountForm
        account={editingAccount}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        title={editingAccount ? 'Modifier le compte' : 'Nouveau compte'}
      />
    </div>
  );
};

export default AccountList;
